// Sign in with Discord. When the server runs with DEV_AUTH this page also offers a name box,
// clearly labelled as not authentication.

import { useState } from "react";
import { useAuth } from "../state/auth.js";
import { signInWithDiscord, supabaseConfigured } from "../lib/supabase.js";
import { Button, Input, Label } from "../components/ui/primitives.js";
import { ClockEmblem, PageShell } from "../components/ui/PageShell.js";

export function SignInPage() {
  const { config, status, error, signInDev } = useAuth();
  const [name, setName] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);

  if (status === "denied") {
    return (
      <PageShell>
        <div className="rounded-xl border border-evil/60 bg-ink-soft/80 p-6 text-center shadow-2xl backdrop-blur-md">
          <h1 className="font-serif text-xl font-semibold text-parchment">Not on the guest list</h1>
          <p className="mt-2 text-sm text-parchment-dim">
            {error ?? "Members of participating Discord servers only."}
          </p>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <div className="space-y-6 rounded-xl border border-brass/25 bg-ink-soft/75 p-7 shadow-2xl backdrop-blur-md">
        <header className="text-center">
          <ClockEmblem />
          <h1 className="mt-4 font-serif text-3xl font-semibold tracking-wide text-parchment">
            Camperdown Clocktower
          </h1>
          <div className="mx-auto mt-3 flex items-center justify-center gap-3 text-brass/70">
            <span className="h-px w-10 bg-brass/40" />
            <span className="text-[10px]">&#9670;</span>
            <span className="h-px w-10 bg-brass/40" />
          </div>
          <p className="mt-3 text-sm text-parchment-dim">
            A town square and Discord bot for Blood on the Clocktower.
          </p>
        </header>

        {supabaseConfigured ? (
          <button
            type="button"
            className="flex w-full items-center justify-center gap-2.5 rounded-md bg-[#5865F2] px-4 py-2.5 text-sm font-semibold text-white shadow-lg transition hover:bg-[#4752c4] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brass"
            onClick={() => void signInWithDiscord().catch((e) => setLocalError(String(e)))}
          >
            <DiscordMark />
            Sign in with Discord
          </button>
        ) : (
          <p className="rounded border border-ink-line bg-ink/60 p-3 text-xs text-parchment-dim">
            Discord sign-in is not avaliable for dev deployment.
          </p>
        )}

        {config?.devAuth && (
          <form
            className="space-y-2 rounded-md border border-brass/40 bg-brass/5 p-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (name.trim()) void signInDev(name.trim());
            }}
          >
            <Label>Local development sign-in</Label>
            <p className="text-[11px] text-parchment-dim">
              Server is running with DEV_AUTH environment, users may join unrestricted.
            </p>
            <div className="flex gap-2">
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" />
              <Button type="submit" disabled={!name.trim()}>
                Enter
              </Button>
            </div>
          </form>
        )}

        {(localError ?? error) && <p className="text-center text-xs text-evil">{localError ?? error}</p>}

        <p className="text-center text-[11px] text-parchment-dim/70">Members of participating Discord servers only.</p>
      </div>
    </PageShell>
  );
}

function DiscordMark() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true">
      <path d="M20.32 4.37A19.8 19.8 0 0 0 15.43 2.9a.07.07 0 0 0-.08.04c-.21.38-.44.87-.6 1.26a18.3 18.3 0 0 0-5.5 0 12.6 12.6 0 0 0-.62-1.26.08.08 0 0 0-.08-.04 19.7 19.7 0 0 0-4.89 1.47.07.07 0 0 0-.03.03C.53 9.05-.32 13.6.1 18.1a.08.08 0 0 0 .03.06 19.9 19.9 0 0 0 6 3.03.08.08 0 0 0 .08-.03c.46-.63.87-1.3 1.23-2a.08.08 0 0 0-.04-.1 13.1 13.1 0 0 1-1.87-.9.08.08 0 0 1 0-.13l.37-.29a.07.07 0 0 1 .08-.01c3.93 1.8 8.18 1.8 12.07 0a.07.07 0 0 1 .08.01l.37.29a.08.08 0 0 1 0 .13c-.6.35-1.22.65-1.87.9a.08.08 0 0 0-.04.1c.36.7.77 1.37 1.23 2a.08.08 0 0 0 .08.03 19.8 19.8 0 0 0 6-3.03.08.08 0 0 0 .03-.06c.5-5.2-.84-9.72-3.55-13.7a.06.06 0 0 0-.03-.03ZM8.02 15.33c-1.18 0-2.16-1.09-2.16-2.42s.96-2.42 2.16-2.42c1.21 0 2.18 1.1 2.16 2.42 0 1.33-.96 2.42-2.16 2.42Zm7.97 0c-1.18 0-2.16-1.09-2.16-2.42s.95-2.42 2.16-2.42c1.21 0 2.18 1.1 2.16 2.42 0 1.33-.95 2.42-2.16 2.42Z" />
    </svg>
  );
}
